/**
 * The launch chapter's screen guides and guided tours — ADR 9044.
 *
 * One entry per route of `./manifest.ts`, keyed by its `path` exactly as declared, in the
 * manifest's order. `tools/screen-guides.mjs` fails `census:features` on a route without an entry,
 * a key unwritten in any locale, or a tour anchor no element carries; `../guides.test.ts` checks
 * each anchor is carried by the screen its step belongs to.
 *
 * The texts (`locale/<locale>/launch.json`, `launch.guide.*`) were written from each screen's
 * code — what it reads, what each figure means, what each control writes, what each state says
 * and why — not from its comments. A screen change that changes what a planner sees changes its
 * guide in the same PR.
 *
 * The chapter is still `disabled: true` in `./manifest.ts`, so the rail draws its screens inert and
 * they are reached by a direct link; the guides describe the screens as they behave, including
 * the states they show while the platform publishes nothing for them.
 */

import type { GuideTable } from '../guides';

export const LAUNCH_GUIDES: GuideTable = {
  'plan': {
    purposeKey: 'launch.guide.plan.purpose',
    readingKey: 'launch.guide.plan.reading',
    actionsKey: 'launch.guide.plan.actions',
    statesKey: 'launch.guide.plan.states',
    sourceKey: 'launch.guide.plan.source',
    tour: [
      { anchor: 'page-status', titleKey: 'launch.guide.plan.tour.state.title', bodyKey: 'launch.guide.plan.tour.state.body' },
      { anchor: 'launch-option-picker', titleKey: 'launch.guide.plan.tour.picker.title', bodyKey: 'launch.guide.plan.tour.picker.body' },
      { anchor: 'launch-run', titleKey: 'launch.guide.plan.tour.run.title', bodyKey: 'launch.guide.plan.tour.run.body' },
      { anchor: 'launch-plan-reconciliation', titleKey: 'launch.guide.plan.tour.reconciliation.title', bodyKey: 'launch.guide.plan.tour.reconciliation.body' },
      { anchor: 'launch-plan-queue', titleKey: 'launch.guide.plan.tour.queue.title', bodyKey: 'launch.guide.plan.tour.queue.body' },
      { anchor: 'launch-plan-approve', titleKey: 'launch.guide.plan.tour.approve.title', bodyKey: 'launch.guide.plan.tour.approve.body' },
      { anchor: 'launch-plan-lines', titleKey: 'launch.guide.plan.tour.lines.title', bodyKey: 'launch.guide.plan.tour.lines.body' },
    ],
  },
  'size-runs': {
    purposeKey: 'launch.guide.sizeRuns.purpose',
    readingKey: 'launch.guide.sizeRuns.reading',
    actionsKey: 'launch.guide.sizeRuns.actions',
    statesKey: 'launch.guide.sizeRuns.states',
    sourceKey: 'launch.guide.sizeRuns.source',
    tour: [
      { anchor: 'page-status', titleKey: 'launch.guide.sizeRuns.tour.state.title', bodyKey: 'launch.guide.sizeRuns.tour.state.body' },
      { anchor: 'launch-option-picker', titleKey: 'launch.guide.sizeRuns.tour.picker.title', bodyKey: 'launch.guide.sizeRuns.tour.picker.body' },
      { anchor: 'launch-run', titleKey: 'launch.guide.sizeRuns.tour.run.title', bodyKey: 'launch.guide.sizeRuns.tour.run.body' },
      { anchor: 'launch-size-runs-gate', titleKey: 'launch.guide.sizeRuns.tour.gate.title', bodyKey: 'launch.guide.sizeRuns.tour.gate.body' },
      { anchor: 'launch-size-runs-doors', titleKey: 'launch.guide.sizeRuns.tour.doors.title', bodyKey: 'launch.guide.sizeRuns.tour.doors.body' },
      { anchor: 'launch-size-runs-control', titleKey: 'launch.guide.sizeRuns.tour.control.title', bodyKey: 'launch.guide.sizeRuns.tour.control.body' },
    ],
  },
  'withholding': {
    purposeKey: 'launch.guide.withholding.purpose',
    readingKey: 'launch.guide.withholding.reading',
    actionsKey: 'launch.guide.withholding.actions',
    statesKey: 'launch.guide.withholding.states',
    sourceKey: 'launch.guide.withholding.source',
    tour: [
      { anchor: 'page-status', titleKey: 'launch.guide.withholding.tour.state.title', bodyKey: 'launch.guide.withholding.tour.state.body' },
      { anchor: 'launch-option-picker', titleKey: 'launch.guide.withholding.tour.picker.title', bodyKey: 'launch.guide.withholding.tour.picker.body' },
      { anchor: 'launch-run', titleKey: 'launch.guide.withholding.tour.run.title', bodyKey: 'launch.guide.withholding.tour.run.body' },
      { anchor: 'launch-withholding-realised', titleKey: 'launch.guide.withholding.tour.realised.title', bodyKey: 'launch.guide.withholding.tour.realised.body' },
      { anchor: 'launch-withholding-figures', titleKey: 'launch.guide.withholding.tour.fraction.title', bodyKey: 'launch.guide.withholding.tour.fraction.body' },
    ],
  },
};
